import React, { Component, createRef } from "react";
import { Link } from "react-router-dom";
import axios from "../../utils/axiosInstance";
import Swal from "sweetalert2";
import Tagify from "@yaireo/tagify";
import "@yaireo/tagify/dist/tagify.css";
import { withRouter } from "../../utils/withRouter";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import "./PostForm.css";

const BRAND_COLOR = "#0049b0";

const DEFAULT_DISCOUNTS = [
  { id: "two-night", label: "2-night plus discount", value: "" },
  { id: "weekly", label: "Weekly discount", value: "" },
  { id: "monthly", label: "Monthly discount", value: "" },
];

const DEFAULT_FORM = {
  customer_id: "",
  property_type_id: "",
  room_type: "",
  category_id: "",
  tag_ids: "",
  facility_ids: "",

  title: "",
  location_main_name: "",
  location_sub_name: "",
  content: "",

  regular_price: "",
  sales_price: "",
  weekend_price: "",

  minimum_night: "",
  extra_charge_on_additional_guest_after: "",
  per_guest_charge: "",
  post_booking_status: "0",
  post_state: "1",

  checkin_time: "",
  checkout_time: "",

  room_number: "",
  beds: "",
  baths: "",
  size: "",
  children: "",
  adults: "",

  real_address: "",
  map_latitude: "",
  map_longitude: "",

  seo_title: "",
  seo_content: "",
  seo_keywords: "",

  feature_image: null,
};

class PostForm extends Component {
  constructor(props) {
    super(props);

    const { id } = props.router.params || {};
    this.isEditMode = Boolean(id);

    this.state = {
      id: id || null,
      form: { ...DEFAULT_FORM },
      discounts: DEFAULT_DISCOUNTS.map((item) => ({ ...item })),
      existingFeatureImage: "",
      featureImagePreview: "",
      galleryItems: [], 
      options: {
        customers: [],
        categories: [],
        property_types: [],
        tags: [],
        facilitys: [],
      },
      // Location Search & Suggestions State
      selectedMainId: null,
      mainSuggestions: [],
      subSuggestions: [],
      showMainSuggestions: false,
      showSubSuggestions: false,

      pageLoading: true,
      postLoading: this.isEditMode,
      saving: false,
    };

    this.tagInputRef = createRef();
    this.facilitysInputRef = createRef();
    this.featureInputRef = createRef();
    this.galleryInputRef = createRef();

    this.tagify = null;
    this.facilityTagify = null;
    this.suppressTagifyChange = false;

    // Drag and Drop References
    this.dragGalleryItem = null;
    this.dragGalleryOverItem = null;

    const baseUrl = axios.defaults.baseURL
      ? axios.defaults.baseURL.replace("/api", "")
      : "";

    this.backendUploadsPath = `${baseUrl}/uploads/posts/`;
  }

  componentDidMount() {
    this.fetchOptions();

    if (this.isEditMode) {
      this.fetchPost(this.state.id);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const optionsChanged =
      prevState.options.tags !== this.state.options.tags ||
      prevState.options.facilitys !== this.state.options.facilitys;

    if (optionsChanged) {
      this.initTagify();
    }

    if (
      prevState.form.tag_ids !== this.state.form.tag_ids ||
      prevState.form.facility_ids !== this.state.form.facility_ids
    ) {
      this.syncTagifyValues();
    }
  }

  componentWillUnmount() {
    if (this.tagify) {
      this.tagify.destroy();
      this.tagify = null;
    }

    if (this.facilityTagify) {
      this.facilityTagify.destroy();
      this.facilityTagify = null;
    }

    if (this.state.featureImagePreview) {
      URL.revokeObjectURL(this.state.featureImagePreview);
    }

    if (this.state.galleryItems) {
      this.state.galleryItems.forEach((item) => {
        if (item.type === 'new' && item.preview) URL.revokeObjectURL(item.preview);
      });
    }
  }

  getDefaultForm = () => ({ ...DEFAULT_FORM });

  getDefaultDiscounts = () =>
    DEFAULT_DISCOUNTS.map((item) => ({ ...item }));

  parseDiscounts = (raw) => {
    if (!raw) {
      return this.getDefaultDiscounts();
    }

    let parsed = raw;

    if (typeof parsed === "string") {
      try {
        parsed = JSON.parse(parsed);
      } catch (error) {
        return this.getDefaultDiscounts();
      }
    }

    if (Array.isArray(parsed)) {
      const mapped = parsed
        .map((item, index) => ({
          id: item?.id || `discount-${Date.now()}-${index}`,
          label: String(item?.label || ""),
          value:
            item?.value === null || item?.value === undefined
              ? ""
              : String(item.value),
        }))
        .filter((item) => item.label || item.value);

      return mapped.length ? mapped : this.getDefaultDiscounts();
    }

    if (typeof parsed === "object") {
      return [
        {
          id: "two-night",
          label: "2-night plus discount",
          value: String(parsed["2_night"] ?? ""),
        },
        {
          id: "weekly",
          label: "Weekly discount",
          value: String(parsed.weekly ?? ""),
        },
        {
          id: "monthly",
          label: "Monthly discount",
          value: String(parsed.monthly ?? ""),
        },
      ];
    }

    return this.getDefaultDiscounts();
  };

  buildFormFromPost = (data) => {
    const safe = data || {};

    return {
      ...this.getDefaultForm(),

      customer_id: safe.customer_id ?? "",
      property_type_id: safe.property_type_id ?? safe.property_type ?? "",
      room_type:
        safe.room_type ||
        (safe.property_type === "Entire Place" ||
        safe.property_type === "Private Room"
          ? safe.property_type
          : ""),

      category_id: safe.category_id ?? "",
      tag_ids: safe.tag_ids ?? "",
      facility_ids: safe.facility_ids ?? "",

      title: safe.title ?? "",
      location_main_name: safe.location_main_name ?? "",
      location_sub_name: safe.location_sub_name ?? "",
      content: safe.content ?? "",

      regular_price: safe.regular_price ?? "",
      sales_price: safe.sales_price ?? safe.price ?? "",
      weekend_price: safe.weekend_price ?? "",

      minimum_night: safe.minimum_night ?? "",
      extra_charge_on_additional_guest_after:
        safe.extra_charge_on_additional_guest_after ?? "",
      per_guest_charge: safe.per_guest_charge ?? "",
      post_booking_status:
        safe.post_booking_status ?? safe.booking_status ?? "0",
      post_state: safe.post_state ?? "1",

      checkin_time: safe.checkin_time ?? "",
      checkout_time: safe.checkout_time ?? "",

      room_number: safe.room_number ?? "",
      beds: safe.beds ?? "",
      baths: safe.baths ?? "",
      size: safe.size ?? "",
      children: safe.children ?? "",
      adults: safe.adults ?? "",

      real_address: safe.real_address ?? safe.location ?? "",
      map_latitude: safe.map_latitude ?? "",
      map_longitude: safe.map_longitude ?? "",

      seo_title: safe.seo_title ?? "",
      seo_content: safe.seo_content ?? "",
      seo_keywords: safe.seo_keywords ?? "",

      feature_image: null,
    };
  };

  fetchOptions = async () => {
    try {
      const [
        customerResponse,
        propertyResponse,
        categoryResponse,
        tagResponse,
        facilityResponse,
      ] = await Promise.all([
        axios.get("/posts/customers").catch(() => ({ data: { data: [] } })),
        axios
          .get("/posts/property_types")
          .catch(() => ({ data: { data: [] } })),
        axios.get("/posts/categories").catch(() => ({ data: { data: [] } })),
        axios.get("/posts/tags").catch(() => ({ data: { data: [] } })),
        axios.get("/posts/facilities").catch(() => ({ data: { data: [] } })),
      ]);

      this.setState(
        {
          options: {
            customers: customerResponse.data?.data || [],
            property_types: propertyResponse.data?.data || [],
            categories: categoryResponse.data?.data || [],
            tags: tagResponse.data?.data || [],
            facilitys: facilityResponse.data?.data || [],
          },
        },
        () => this.initTagify()
      );
    } catch (error) {
      console.error("Option loading error:", error);
      Swal.fire(
        "Warning",
        "Some dropdown options could not be loaded.",
        "warning"
      );
    } finally {
      this.setState({ pageLoading: false });
    }
  };

  fetchPost = async (id) => {
    try {
      const response = await axios.get(`/posts/${id}`);

      if (response.data?.status !== "success") {
        Swal.fire("Error", "Post not found", "error");
        return;
      }

      const data = response.data.data || {};
      let galleryItems = [];

      if (data.gallery_images) {
        try {
          const parsed =
            typeof data.gallery_images === "string"
              ? JSON.parse(data.gallery_images)
              : data.gallery_images;

          if (Array.isArray(parsed)) {
            galleryItems = parsed.filter(Boolean).map(img => ({
               type: 'existing',
               filename: img,
               preview: `${this.backendUploadsPath}${img}`
            }));
          }
        } catch (error) {
          console.error("Gallery parse error:", error);
        }
      }

      this.setState(
        {
          form: this.buildFormFromPost(data),
          discounts: this.parseDiscounts(data.more_night_discount),
          existingFeatureImage: data.feature_image
            ? `${this.backendUploadsPath}${data.feature_image}`
            : "",
          featureImagePreview: "",
          galleryItems,
          selectedMainId: data.location_main_id ?? null,
        },
        () => {
          this.initTagify();
          this.syncTagifyValues();
        }
      );
    } catch (error) {
      console.error("Post fetch error:", error);
      Swal.fire("Error", "Failed to fetch post data", "error");
    } finally {
      this.setState({ postLoading: false });
    }
  };

  // ----------------------------------------------------
  // Location API Suggestions Handlers
  // ----------------------------------------------------

  handleMainLocationChange = async (e) => {
    const value = e.target.value;
    this.setState((prevState) => ({
      form: {
        ...prevState.form,
        location_main_name: value,
        location_sub_name: "", // Reset sub location when main location changes
      },
      selectedMainId: null,
      showMainSuggestions: true,
    }));

    if (!value.trim()) {
      this.setState({ mainSuggestions: [], showMainSuggestions: false });
      return;
    }

    try {
      const res = await axios.get("get_all_main_location");
      const list = res.data?.data || res.data || [];
      const query = value.toLowerCase();

      const filtered = Array.isArray(list)
        ? list.filter((item) => {
            const name = item.location_main_name || item.name || "";
            return name.toLowerCase().includes(query);
          })
        : [];

      this.setState({ mainSuggestions: filtered, showMainSuggestions: true });
    } catch (err) {
      console.error("Error fetching main locations:", err);
    }
  };

  selectMainLocation = (item) => {
    const name = item.location_main_name || item.name || "";
    const id = item.id || null;

    this.setState((prevState) => ({
      form: {
        ...prevState.form,
        location_main_name: name,
        location_sub_name: "",
      },
      selectedMainId: id,
      mainSuggestions: [],
      showMainSuggestions: false,
    }));
  };

  handleSubLocationChange = async (e) => {
    const value = e.target.value;
    this.setState((prevState) => ({
      form: {
        ...prevState.form,
        location_sub_name: value,
      },
      showSubSuggestions: true,
    }));

    const { selectedMainId } = this.state;
    if (!value.trim() || !selectedMainId) {
      this.setState({ subSuggestions: [], showSubSuggestions: false });
      return;
    }

    try {
      const res = await axios.get(`get_sub_location_by_main_location_id?id=${selectedMainId}`);
      const list = res.data?.data || res.data || [];
      const query = value.toLowerCase();

      const filtered = Array.isArray(list)
        ? list.filter((item) => {
            const name = item.location_sub_name || item.name || "";
            return name.toLowerCase().includes(query);
          })
        : [];

      this.setState({ subSuggestions: filtered, showSubSuggestions: true });
    } catch (err) {
      console.error("Error fetching sub locations:", err);
    }
  };

  selectSubLocation = (item) => {
    const name = item.location_sub_name || item.name || "";

    this.setState((prevState) => ({
      form: {
        ...prevState.form,
        location_sub_name: name,
      },
      subSuggestions: [],
      showSubSuggestions: false,
    }));
  };

  initTagify = () => {
    const tagWhitelist = this.state.options.tags
      .map((item) =>
        typeof item === "string"
          ? item
          : item?.tag_name || item?.name || item?.value
      )
      .filter(Boolean);

    const facilityWhitelist = this.state.options.facilitys
      .map((item) =>
        typeof item === "string"
          ? item
          : item?.facility_name || item?.name || item?.value
      )
      .filter(Boolean);

    if (this.tagInputRef.current) {
      if (this.tagify) {
        this.tagify.settings.whitelist = tagWhitelist;
      } else {
        this.tagify = new Tagify(this.tagInputRef.current, {
          whitelist: tagWhitelist,
          maxTags: 10,
          dropdown: {
            maxItems: 20,
            classname: "tags-look",
            enabled: 0,
            closeOnSelect: false,
          },
        });

        this.tagify.on("change", (event) => {
          if (this.suppressTagifyChange) return;

          let tags = [];

          if (event.detail.value) {
            try {
              tags = JSON.parse(event.detail.value).map((tag) => tag.value);
            } catch (error) {
              console.error("Tagify parse error:", error);
            }
          }

          this.setState((prevState) => ({
            form: {
              ...prevState.form,
              tag_ids: tags.join(","),
            },
          }));
        });
      }
    }

    if (this.facilitysInputRef.current) {
      if (this.facilityTagify) {
        this.facilityTagify.settings.whitelist = facilityWhitelist;
      } else {
        this.facilityTagify = new Tagify(this.facilitysInputRef.current, {
          whitelist: facilityWhitelist,
          maxTags: 3000,
          dropdown: {
            maxItems: 3000,
            classname: "tags-look",
            enabled: 0,
            closeOnSelect: false,
          },
        });

        this.facilityTagify.on("change", (event) => {
          if (this.suppressTagifyChange) return;

          let facilitys = [];

          if (event.detail.value) {
            try {
              facilitys = JSON.parse(event.detail.value).map(
                (item) => item.value
              );
            } catch (error) {
              console.error("Tagify parse error:", error);
            }
          }

          this.setState((prevState) => ({
            form: {
              ...prevState.form,
              facility_ids: facilitys.join(","),
            },
          }));
        });
      }
    }

    this.syncTagifyValues();
  };

  setTagifyValues = (instance, value) => {
    if (!instance) return;

    const values = Array.isArray(value)
      ? value
      : String(value || "")
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);

    this.suppressTagifyChange = true;

    instance.removeAllTags();

    if (values.length) {
      instance.addTags(values);
    }

    this.suppressTagifyChange = false;
  };

  syncTagifyValues = () => {
    if (!this.tagify && !this.facilityTagify) return;

    this.setTagifyValues(this.tagify, this.state.form.tag_ids);
    this.setTagifyValues(this.facilityTagify, this.state.form.facility_ids);
  };

  handleChange = (event) => {
    const { name, value, type, files, multiple, checked } = event.target;

    let updatedValue = value;

    if (type === "checkbox") {
      updatedValue = checked ? "1" : "0";
    }

    if (type === "file") {
      updatedValue = multiple ? Array.from(files || []) : files?.[0] || null;
    }

    this.setState((prevState) => ({
      form: {
        ...prevState.form,
        [name]: updatedValue,
      },
    }));
  };

  handleDiscountChange = (id, field, value) => {
    this.setState((prevState) => ({
      discounts: prevState.discounts.map((discount) =>
        discount.id === id ? { ...discount, [field]: value } : discount
      ),
    }));
  };

  addDiscount = () => {
    this.setState((prevState) => ({
      discounts: [
        ...prevState.discounts,
        {
          id: `discount-${Date.now()}`,
          label: "",
          value: "",
        },
      ],
    }));
  };

  removeDiscount = (id) => {
    this.setState((prevState) => ({
      discounts:
        prevState.discounts.length <= 1
          ? prevState.discounts
          : prevState.discounts.filter((discount) => discount.id !== id),
    }));
  };

  handleFeatureImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);

    this.setState((prevState) => {
      if (prevState.featureImagePreview) {
        URL.revokeObjectURL(prevState.featureImagePreview);
      }

      return {
        featureImagePreview: previewUrl,
        form: {
          ...prevState.form,
          feature_image: file,
        },
      };
    });
  };

  handleGalleryImageChange = (event) => {
    const files = Array.from(event.target.files || []);

    const newItems = files.map((file) => ({
      type: 'new',
      file: file,
      preview: URL.createObjectURL(file),
    }));

    this.setState((prevState) => ({
      galleryItems: [...prevState.galleryItems, ...newItems],
    }));

    if (this.galleryInputRef.current) {
      this.galleryInputRef.current.value = "";
    }
  };

  clearFeatureImage = () => {
    if (this.featureInputRef.current) {
      this.featureInputRef.current.value = "";
    }

    this.setState((prevState) => {
      if (prevState.featureImagePreview) {
        URL.revokeObjectURL(prevState.featureImagePreview);
      }

      return {
        featureImagePreview: "",
        form: {
          ...prevState.form,
          feature_image: null,
        },
      };
    });
  };

  // ----------------------------------------------------
  // Drag, Drop & Removal Logic (Unified List)
  // ----------------------------------------------------

  onGalleryDragStart = (e, index) => {
    this.dragGalleryItem = index;
  };

  onGalleryDragEnter = (e, index) => {
    this.dragGalleryOverItem = index;
  };

  onGalleryDragEnd = () => {
    if (this.dragGalleryItem !== null && this.dragGalleryOverItem !== null) {
      const _galleryItems = [...this.state.galleryItems];
      const draggedItem = _galleryItems.splice(this.dragGalleryItem, 1)[0];
      _galleryItems.splice(this.dragGalleryOverItem, 0, draggedItem);

      this.setState({ galleryItems: _galleryItems });
    }
    this.dragGalleryItem = null;
    this.dragGalleryOverItem = null;
  };

  removeGalleryImage = async (indexToRemove) => {
    const result = await Swal.fire({
      title: "Remove Image?",
      text: "Are you sure you want to remove this image? (Changes take effect on save)",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626", // Bookly Danger Red
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, remove it!",
    });

    if (result.isConfirmed) {
      this.setState((prevState) => {
        const newItems = [...prevState.galleryItems];
        const removed = newItems.splice(indexToRemove, 1)[0];

        if (removed.type === "new" && removed.preview) {
          URL.revokeObjectURL(removed.preview);
        }

        return { galleryItems: newItems };
      });
    }
  };

  getPostStateLabel = (stateValue) => {
    const labels = {
      "0": "Inactive",
      "1": "Published",
      "2": "Draft",
      "3": "Request",
      "4": "Review Completed",
    };

    return labels[String(stateValue)] || "Unknown";
  };

  // Combines main and sub locations into a location string (e.g. "Mohakhali, Dhaka")
  getCombinedLocationString = () => {
    const main = String(this.state.form.location_main_name || "").trim();
    const sub = String(this.state.form.location_sub_name || "").trim();

    if (sub && main) {
      return `${sub} | ${main}`;
    }
    return sub || main;
  };

  // Automatically concatenates title with location string like "Title at Sub Location, Main Location"
  getCombinedTitle = (titleValue) => {
    const title = String(titleValue ?? "").trim();
    const locationStr = this.getCombinedLocationString();

    if (!locationStr) return titleValue ?? "";
    if (!title) return locationStr;

    const suffix = ` at ${locationStr}`;
    if (title.toLocaleLowerCase().endsWith(suffix.toLocaleLowerCase())) {
      return title;
    }

    return `${title} at ${locationStr}`;
  };

  getSubmitButtonLabel = () => {
    const status = String(this.state.form.post_state);
    const prefix = this.isEditMode ? "Update" : "Save";

    if (status === "2") return this.isEditMode ? "Update Draft" : "Save Draft";
    if (status === "3") return this.isEditMode ? "Update Request" : "Submit for Review";
    if (status === "4") return this.isEditMode ? "Update Review" : "Save Review Completed";
    if (status === "0") return `${prefix} Inactive`;
    return this.isEditMode ? "Update Post" : "Publish Post";
  };

  validateForm = () => {
    const { form } = this.state;

    if (String(form.post_state) === "2") return null;

    if (!form.customer_id) {
      return "Please select a customer.";
    }

    if (!form.category_id) {
      return "Please select a category.";
    }

    if (!form.room_type) {
      return "Please select Entire Place or Private Room.";
    }

    if (!String(form.title || "").trim()) {
      return "Please enter a listing title.";
    }

    if (!form.sales_price && !form.regular_price) {
      return "Please enter at least one property price.";
    }

    return null;
  };

  handleSubmit = async (event) => {
    event.preventDefault();

    const validationError = this.validateForm();

    if (validationError) {
      Swal.fire("Validation", validationError, "warning");
      return;
    }

    const { form, id, discounts, galleryItems } = this.state;

    const cleanedDiscounts = discounts
      .map((discount) => ({
        label: String(discount.label || "").trim(),
        value: String(discount.value || "").trim(),
      }))
      .filter((discount) => discount.label || discount.value);

    const formData = new FormData();

    const textFields = [
      "customer_id",
      "property_type_id",
      "category_id",
      "tag_ids",
      "facility_ids",
      "title",
      "location_main_name",
      "location_sub_name",
      "content",
      "regular_price",
      "sales_price",
      "weekend_price",
      "minimum_night",
      "extra_charge_on_additional_guest_after",
      "per_guest_charge",
      "post_booking_status",
      "post_state",
      "checkin_time",
      "checkout_time",
      "room_number",
      "beds",
      "baths",
      "size",
      "children",
      "adults",
      "real_address",
      "map_latitude",
      "map_longitude",
      "seo_title",
      "seo_content",
      "seo_keywords",
    ];

    textFields.forEach((key) => {
      // Concatenate location with title during submission
      const value = key === "title"
        ? this.getCombinedTitle(form.title)
        : form[key];

      if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    });

    formData.append("room_type", form.room_type);
    formData.append("property_type", form.property_type_id);
    formData.append("booking_status", form.post_booking_status);
    formData.append("more_night_discount", JSON.stringify(cleanedDiscounts));

    if (form.feature_image instanceof File) {
      formData.append("feature_image", form.feature_image);
    }

    const galleryOrder = [];
    galleryItems.forEach((item) => {
      if (item.type === "existing") {
        galleryOrder.push(item.filename);
      } else if (item.type === "new" && item.file instanceof File) {
        galleryOrder.push("NEW_FILE");
        formData.append("gallery_images[]", item.file);
      }
    });

    formData.append("gallery_order", JSON.stringify(galleryOrder));

    try {
      this.setState({ saving: true });

      const endpoint = this.isEditMode ? `/posts/${id}?_method=PUT` : "/posts";

      const response = await axios.post(endpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data?.status === "success") {
        await Swal.fire({
          icon: "success",
          title:
            String(form.post_state) === "2"
              ? (this.isEditMode ? "Draft Updated" : "Draft Saved")
              : String(form.post_state) === "3"
                ? "Request Submitted"
                : String(form.post_state) === "4"
                  ? "Review Completed"
                  : String(form.post_state) === "0"
                    ? "Inactive Post Saved"
                    : this.isEditMode
                      ? "Post Updated"
                      : "Post Published",
          text:
            response.data.message ||
            (String(form.post_state) === "2"
              ? "The draft has been saved successfully. You can complete it later."
              : String(form.post_state) === "3"
                ? "The listing has been submitted for admin review."
                : String(form.post_state) === "4"
                  ? "The listing has been saved with Review Completed status."
                  : String(form.post_state) === "0"
                    ? "The listing has been saved as inactive."
                    : this.isEditMode
                      ? "The listing has been updated successfully."
                      : "The listing has been published successfully."),
          confirmButtonColor: BRAND_COLOR,
        });

        this.props.router.navigate("/admin/posts");
        return;
      }

      Swal.fire(
        "Error",
        response.data?.message || "Failed to save post.",
        "error"
      );
    } catch (error) {
      console.error("Post save error:", error);

      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          "Failed to save post. Please try again.",
        "error"
      );
    } finally {
      this.setState({ saving: false });
    }
  };

  renderSectionHeader = (eyebrow, title, description) => (
    <div className="post-section-heading">
      <div>
        <div className="post-section-eyebrow">{eyebrow}</div>
        <h5>{title}</h5>
        {description ? <p>{description}</p> : null}
      </div>
    </div>
  );

  renderField = ({
    label,
    name,
    type = "text",
    placeholder = "",
    col = "col-md-6",
    required = false,
    min,
    step,
    help,
  }) => {
    const value = this.state.form[name] ?? "";

    return (
      <div className={col}>
        <div className="post-field">
          <label htmlFor={`post-${name}`} className="post-label">
            {label} {required ? <span>*</span> : null}
          </label>

          <input
            id={`post-${name}`}
            type={type}
            name={name}
            value={value}
            onChange={this.handleChange}
            className="form-control post-control"
            placeholder={placeholder}
            required={required}
            min={min}
            step={step}
          />

          {help ? <div className="post-help">{help}</div> : null}
        </div>
      </div>
    );
  };

  renderSelect = ({
    label,
    name,
    options,
    placeholder,
    col = "col-md-6",
    required = false,
  }) => {
    const value = this.state.form[name] ?? "";

    return (
      <div className={col}>
        <div className="post-field">
          <label htmlFor={`post-${name}`} className="post-label">
            {label} {required ? <span>*</span> : null}
          </label>

          <select
            id={`post-${name}`}
            name={name}
            value={value}
            onChange={this.handleChange}
            className="form-select post-control"
            required={required}
          >
            <option value="">{placeholder}</option>

            {options.map((option) => (
              <option key={String(option.value)} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    );
  };

  renderSwitch = ({
    label,
    name,
    description,
    activeText,
    inactiveText,
    activeValue = "1",
    inactiveValue = "0",
  }) => {
    const active = String(this.state.form[name]) === String(activeValue);

    return (
      <div className={`post-setting-row ${active ? "is-active" : ""}`}>
        <div className="post-setting-copy">
          <div className="post-setting-title">{label}</div>
          <div className="post-setting-description">{description}</div>
          <div className={`post-setting-status ${active ? "active" : ""}`}>
            {active ? activeText : inactiveText}
          </div>
        </div>

        <label className="post-switch" aria-label={label}>
          <input
            type="checkbox"
            checked={active}
            onChange={(event) =>
              this.setState((prevState) => ({
                form: {
                  ...prevState.form,
                  [name]: event.target.checked
                    ? String(activeValue)
                    : String(inactiveValue),
                },
              }))
            }
          />
          <span className="post-switch-slider" />
        </label>
      </div>
    );
  };

  renderPostStatusSelect = () => {
    const { post_state } = this.state.form;
    const statusInfo = {
      "2": "Drafts can be saved before all required listing information is complete.",
      "3": "Submit complete listing details for review by an administrator.",
      "4": "Use this after the administrator has completed the review.",
      "1": "The listing is published and available according to your site rules.",
      "0": "The listing remains saved but is marked inactive.",
    };

    return (
      <div className="post-status-field">
        <label htmlFor="post-post_state" className="post-label">
          Listing Status <span>*</span>
        </label>
        <select
          id="post-post_state"
          name="post_state"
          value={String(post_state ?? "1")}
          onChange={this.handleChange}
          className={`form-select post-control post-status-select status-value-${String(post_state ?? "1")}`}
        >
          <option value="2">Draft</option>
          <option value="3">Request — Awaiting Admin Review</option>
          <option value="4">Review Completed</option>
          <option value="1">Published</option>
          <option value="0">Inactive</option>
        </select>
        <div className="post-help">{statusInfo[String(post_state)] || statusInfo["1"]}</div>
        <div className={`post-status-current status-value-${String(post_state ?? "1")}`}>
          <span className="post-status-current-dot" />
          Current selection: <strong>{this.getPostStateLabel(post_state)}</strong>
        </div>
      </div>
    );
  };

  renderImageCard = () => {
    const { form, existingFeatureImage, featureImagePreview } = this.state;
    const preview = featureImagePreview || existingFeatureImage;

    return (
      <div className="media-card">
        <div className="media-card-top">
          <div>
            <div className="post-section-eyebrow">Featured media</div>
            <h6>Cover Image</h6>
            <p>Use a clear image that represents the property.</p>
          </div>

          <button
            type="button"
            className="btn btn-sm post-outline-btn"
            onClick={() => this.featureInputRef.current?.click()}
          >
            {preview ? "Change Image" : "Choose Image"}
          </button>
        </div>

        <input
          ref={this.featureInputRef}
          type="file"
          name="feature_image"
          accept="image/*"
          className="d-none"
          onChange={this.handleFeatureImageChange}
        />

        {preview ? (
          <div className="feature-preview-wrap">
            <img src={preview} alt="Featured preview" />
            {form.feature_image ? (
              <button
                type="button"
                className="feature-remove-btn"
                onClick={this.clearFeatureImage}
              >
                Clear selection
              </button>
            ) : (
              <div className="feature-current-badge">Current image</div>
            )}
          </div>
        ) : (
          <button
            type="button"
            className="image-dropzone"
            onClick={() => this.featureInputRef.current?.click()}
          >
            <span className="image-dropzone-icon">＋</span>
            <strong>Add feature image</strong>
            <small>JPG, PNG or WEBP</small>
          </button>
        )}
      </div>
    );
  };

  renderGalleryCard = () => {
    const { galleryItems } = this.state;

    return (
      <div className="media-card">
        <div className="media-card-top">
          <div>
            <div className="post-section-eyebrow">Property media</div>
            <h6>Gallery Images</h6>
            <p>
              Upload and manage your gallery. Drag & drop images to re-arrange
              them.
            </p>
          </div>

          <button
            type="button"
            className="btn btn-sm post-outline-btn"
            onClick={() => this.galleryInputRef.current?.click()}
          >
            Add Gallery
          </button>
        </div>

        <input
          ref={this.galleryInputRef}
          type="file"
          name="gallery_images"
          accept="image/*"
          multiple
          className="d-none"
          onChange={this.handleGalleryImageChange}
        />

        {galleryItems.length > 0 ? (
          <div className="dnd-gallery-grid">
            {galleryItems.map((item, index) => (
              <div
                className="dnd-gallery-item"
                key={`gallery-item-${index}`}
                draggable
                onDragStart={(e) => this.onGalleryDragStart(e, index)}
                onDragEnter={(e) => this.onGalleryDragEnter(e, index)}
                onDragEnd={this.onGalleryDragEnd}
                onDragOver={(e) => e.preventDefault()}
              >
                <img src={item.preview} alt={`Gallery block ${index + 1}`} />
                <button
                  type="button"
                  className="remove-gallery-btn"
                  onClick={() => this.removeGalleryImage(index)}
                  title="Remove image"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="media-empty">No gallery images selected.</div>
        )}
      </div>
    );
  };

  renderDiscountBuilder = () => {
    const { discounts } = this.state;

    return (
      <div className="discount-builder">
        <div className="discount-builder-head">
          <div>
            <h6>Discount On More Nights</h6>
            <p>
              Create percentage discounts for longer stays. The saved value is
              stored in <code>more_night_discount</code>.
            </p>
          </div>

          <button
            type="button"
            className="btn btn-sm post-outline-btn"
            onClick={this.addDiscount}
          >
            + Add Discount
          </button>
        </div>

        <div className="discount-list">
          {discounts.map((discount, index) => (
            <div className="discount-row" key={discount.id}>
              <div className="discount-index">{index + 1}</div>

              <input
                type="text"
                value={discount.label}
                onChange={(event) =>
                  this.handleDiscountChange(
                    discount.id,
                    "label",
                    event.target.value
                  )
                }
                className="form-control post-control"
                placeholder="Discount name"
              />

              <div className="discount-percent-wrap">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={discount.value}
                  onChange={(event) =>
                    this.handleDiscountChange(
                      discount.id,
                      "value",
                      event.target.value
                    )
                  }
                  className="form-control post-control"
                  placeholder="0"
                />
                <span>%</span>
              </div>

              <button
                type="button"
                className="discount-remove-btn"
                onClick={() => this.removeDiscount(discount.id)}
                disabled={discounts.length <= 1}
                title="Remove discount"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  render() {
    const { form, options, saving, pageLoading, postLoading, mainSuggestions, subSuggestions, showMainSuggestions, showSubSuggestions } = this.state;

    if ((pageLoading && !this.isEditMode) || (this.isEditMode && postLoading)) {
      return (
        <div className="pc-container">
          <div className="pc-content">
            <div className="post-loading-card">
              <div className="post-spinner" />
              <div>
                <strong>Loading post...</strong>
                <p>Please wait while the listing data is prepared.</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    const roomTypeOptions = [
      {
        value: "Entire Place",
        label: "Entire Place",
      },
      {
        value: "Private Room",
        label: "Private Room",
      },
    ];

    const propertyTypeOptions = options.property_types.map((item) => ({
      value: item.id,
      label: item.property_type_name,
    }));

    const categoryOptions = options.categories.map((item) => ({
      value: item.id,
      label: item.category_name,
    }));

    const customerOptions = options.customers.map((item) => ({
      value: item.id,
      label: item.full_name || item.name || `Customer #${item.id}`,
    }));

    return (
      <div className="pc-container post-form-page">
        <div className="pc-content">
          <div className="post-form-topbar">
            <div>
              <div className="post-breadcrumb">
                <Link to="/admin/posts">Post</Link>
                <span>/</span>
                <span>{this.isEditMode ? "Edit" : "Add"}</span>
              </div>

              <h3>{this.isEditMode ? "Edit Listing" : "Add New Listing"}</h3>

              <p>
                Manage the property details, pricing, booking rules and SEO
                settings from one place.
              </p>
            </div>

            <Link to="/admin/posts" className="btn post-list-btn">
              Back to List
            </Link>
          </div>

          <form onSubmit={this.handleSubmit} noValidate>
            {/* GENERAL */}
            <div className="post-card">
              {this.renderSectionHeader(
                "01",
                "General Information",
                "Start with the listing title, location and property description."
              )}

              <div className="row g-4">
                {this.renderField({
                  label: "Title",
                  name: "title",
                  placeholder: "e.g. Modern apartment",
                  col: "col-12",
                  required: String(form.post_state) !== "2",
                })}

                {/* Main Location & Sub Location (50% / 50% width) */}
                <div className="col-md-6">
                  <div className="post-field post-autocomplete-wrap">
                    <label htmlFor="post-location_main_name" className="post-label">
                      Main Location (Optional)
                    </label>
                    <input
                      id="post-location_main_name"
                      type="text"
                      name="location_main_name"
                      value={form.location_main_name}
                      onChange={this.handleMainLocationChange}
                      onFocus={() => {
                        if (form.location_main_name.trim()) {
                          this.setState({ showMainSuggestions: true });
                        }
                      }}
                      className="form-control post-control"
                      placeholder="Type or search main location"
                      autoComplete="off"
                    />
                    {showMainSuggestions && mainSuggestions.length > 0 && (
                      <div className="post-suggestions-dropdown">
                        {mainSuggestions.map((item, index) => (
                          <div
                            key={`main-sug-${index}`}
                            className="post-suggestion-item"
                            onClick={() => this.selectMainLocation(item)}
                          >
                            {item.location_main_name || item.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="post-field post-autocomplete-wrap">
                    <label htmlFor="post-location_sub_name" className="post-label">
                      Sub Location (Optional)
                    </label>
                    <input
                      id="post-location_sub_name"
                      type="text"
                      name="location_sub_name"
                      value={form.location_sub_name}
                      onChange={this.handleSubLocationChange}
                      onFocus={() => {
                        if (form.location_sub_name.trim()) {
                          this.setState({ showSubSuggestions: true });
                        }
                      }}
                      className="form-control post-control"
                      placeholder="Type or search sub location"
                      autoComplete="off"
                    />
                    {showSubSuggestions && subSuggestions.length > 0 && (
                      <div className="post-suggestions-dropdown">
                        {subSuggestions.map((item, index) => (
                          <div
                            key={`sub-sug-${index}`}
                            className="post-suggestion-item"
                            onClick={() => this.selectSubLocation(item)}
                          >
                            {item.location_sub_name || item.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-12">
                  <div className="post-field">
                    <label className="post-label">Content / Description</label>
                    <div className="post-editor-shell">
                      <CKEditor
                        editor={ClassicEditor}
                        data={form.content || ""}
                        onChange={(event, editor) => {
                          const data = editor.getData();

                          this.setState((prevState) => ({
                            form: {
                              ...prevState.form,
                              content: data,
                            },
                          }));
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* PROPERTY */}
            <div className="post-card">
              {this.renderSectionHeader(
                "02",
                "Property & Classification",
                "Select the customer, property type, room type and category."
              )}

              <div className="row g-4">
                {this.renderSelect({
                  label: "Customer",
                  name: "customer_id",
                  options: customerOptions,
                  placeholder: "Select customer",
                  required: true,
                })}

                {this.renderSelect({
                  label: "Property Type",
                  name: "property_type_id",
                  options: propertyTypeOptions,
                  placeholder: "Select property type",
                })}

                {this.renderSelect({
                  label: "Category",
                  name: "category_id",
                  options: categoryOptions,
                  placeholder: "Select category",
                  required: true,
                })}

                {this.renderSelect({
                  label: "Entire Place or Private Room",
                  name: "room_type",
                  options: roomTypeOptions,
                  placeholder: "Select room type",
                  required: true,
                })}

                <div className="col-12">
                  <div className="post-field">
                    <label className="post-label">Tags</label>
                    <input
                      ref={this.tagInputRef}
                      type="text"
                      className="form-control post-control tagify-host"
                      placeholder="Type and select tags"
                    />
                    <div className="post-help">
                      Search available tags or enter the allowed values.
                    </div>
                  </div>
                </div>

                <div className="col-12">
                  <div className="post-field">
                    <label className="post-label">Facilities</label>
                    <input
                      ref={this.facilitysInputRef}
                      type="text"
                      className="form-control post-control tagify-host"
                      placeholder="Type and select facilities"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* PRICING */}
            <div className="post-card">
              {this.renderSectionHeader(
                "03",
                "Pricing & Stay Rules",
                "Configure nightly pricing, minimum stay and additional guest charges."
              )}

              <div className="row g-4">
                {this.renderField({
                  label: "Regular Price (BDT)",
                  name: "regular_price",
                  type: "number",
                  min: "0",
                  step: "0.01",
                  placeholder: "0",
                })}

                {this.renderField({
                  label: "Sales Price (BDT)",
                  name: "sales_price",
                  type: "number",
                  min: "0",
                  step: "0.01",
                  placeholder: "0",
                })}

                {this.renderField({
                  label: "Weekend Price (BDT)",
                  name: "weekend_price",
                  type: "number",
                  min: "0",
                  step: "0.01",
                  placeholder: "Friday & Saturday rate",
                })}

                {this.renderField({
                  label: "Minimum Nights",
                  name: "minimum_night",
                  type: "number",
                  min: "1",
                  step: "1",
                  placeholder: "1",
                })}

                {this.renderField({
                  label: "Additional Guest After",
                  name: "extra_charge_on_additional_guest_after",
                  type: "number",
                  min: "0",
                  step: "1",
                  placeholder: "Number of included guests",
                })}

                {this.renderField({
                  label: "Per Guest Charge (BDT)",
                  name: "per_guest_charge",
                  type: "number",
                  min: "0",
                  step: "0.01",
                  placeholder: "0",
                })}
              </div>
            </div>

            {/* AVAILABILITY */}
            <div className="post-card">
              {this.renderSectionHeader(
                "04",
                "Availability & Location",
                "Set check-in/out times, address and map coordinates."
              )}

              <div className="row g-4">
                {this.renderField({
                  label: "Check-in Time",
                  name: "checkin_time",
                  type: "time",
                })}

                {this.renderField({
                  label: "Check-out Time",
                  name: "checkout_time",
                  type: "time",
                })}

                <div className="col-12">
                  <div className="post-field">
                    <label htmlFor="post-real-address" className="post-label">
                      Real Address
                    </label>

                    <textarea
                      id="post-real-address"
                      name="real_address"
                      value={form.real_address}
                      onChange={this.handleChange}
                      className="form-control post-control post-textarea"
                      placeholder="Enter full property address"
                      rows={3}
                    />
                  </div>
                </div>

                {this.renderField({
                  label: "Latitude",
                  name: "map_latitude",
                  placeholder: "23.xxxxxx",
                  col: "col-md-6",
                })}

                {this.renderField({
                  label: "Longitude",
                  name: "map_longitude",
                  placeholder: "90.xxxxxx",
                  col: "col-md-6",
                })}
              </div>
            </div>

            {/* SPECIFICATIONS */}
            <div className="post-card">
              {this.renderSectionHeader(
                "05",
                "Specifications",
                "Define the capacity and physical details guests will see."
              )}

              <div className="row g-4">
                {this.renderField({
                  label: "No. of Rooms",
                  name: "room_number",
                  type: "number",
                  min: "0",
                  step: "1",
                  col: "col-md-4",
                })}

                {this.renderField({
                  label: "No. of Beds",
                  name: "beds",
                  type: "number",
                  min: "0",
                  step: "1",
                  col: "col-md-4",
                })}

                {this.renderField({
                  label: "No. of Bathrooms",
                  name: "baths",
                  type: "number",
                  min: "0",
                  step: "0.5",
                  col: "col-md-4",
                })}

                {this.renderField({
                  label: "Square Size",
                  name: "size",
                  type: "text",
                  placeholder: "e.g. 1200 sq ft",
                  col: "col-md-4",
                })}

                {this.renderField({
                  label: "Adults",
                  name: "adults",
                  type: "number",
                  min: "0",
                  step: "1",
                  col: "col-md-4",
                })}

                {this.renderField({
                  label: "Children",
                  name: "children",
                  type: "number",
                  min: "0",
                  step: "1",
                  col: "col-md-4",
                })}
              </div>
            </div>

            {/* BOOKING / ADVANCED */}
            <div className="post-card">
              {this.renderSectionHeader(
                "06",
                "Booking & Advanced Settings",
                "Control automatic booking, listing activity and long-stay discounts."
              )}

              <div className="post-setting-stack">
                {this.renderSwitch({
                  label: "Auto Accept Booking",
                  name: "post_booking_status",
                  description:
                    "Turn this on to accept eligible booking requests automatically.",
                  activeText: "Auto Accept Booking (ON)",
                  inactiveText: "Auto Accept Booking (OFF)",
                })}

                {this.renderPostStatusSelect()}
              </div>

              <div className="mt-4">{this.renderDiscountBuilder()}</div>
            </div>

            {/* MEDIA */}
            <div className="post-card">
              {this.renderSectionHeader(
                "07",
                "Images",
                "Upload a featured image and optional gallery images."
              )}

              <div className="row g-4">
                <div className="col-lg-6">{this.renderImageCard()}</div>
                <div className="col-lg-6">{this.renderGalleryCard()}</div>
              </div>
            </div>

            {/* SEO */}
            <div className="post-card">
              {this.renderSectionHeader(
                "08",
                "SEO Settings",
                "Add search-friendly metadata for the listing page."
              )}

              <div className="row g-4">
                {this.renderField({
                  label: "SEO Title",
                  name: "seo_title",
                  placeholder: "SEO title",
                  col: "col-12",
                })}

                <div className="col-md-6">
                  <div className="post-field">
                    <label className="post-label">SEO Content</label>
                    <textarea
                      name="seo_content"
                      value={form.seo_content}
                      onChange={this.handleChange}
                      className="form-control post-control post-textarea"
                      rows={4}
                      placeholder="SEO description"
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="post-field">
                    <label className="post-label">SEO Keywords</label>
                    <textarea
                      name="seo_keywords"
                      value={form.seo_keywords}
                      onChange={this.handleChange}
                      className="form-control post-control post-textarea"
                      rows={4}
                      placeholder="keyword one, keyword two"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* ACTION BAR */}
            <div className="post-form-actions">
              <div className="post-form-actions-copy">
                <strong>{this.getSubmitButtonLabel()}</strong>
                <span>
                  {String(form.post_state) === "2"
                    ? "Save an incomplete draft and finish it later."
                    : String(form.post_state) === "3"
                      ? "Submit the completed listing for admin review."
                      : "Check the listing details before saving."}
                </span>
              </div>

              <div className="post-form-action-buttons">
                <Link to="/admin/posts" className="btn post-cancel-btn">
                  Cancel
                </Link>

                <button
                  type="submit"
                  className="btn post-save-btn"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="post-spinner-small" />
                      Saving...
                    </>
                  ) : (
                    this.getSubmitButtonLabel()
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  }
}

export default withRouter(PostForm);
